'use client';

import React from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, Snackbar, Stack, Typography } from '@mui/material';
import GlassFormField from '@/components/design-studio/GlassFormField';
import { MarketingFormPanel } from '@/components/design-studio/GlassFormScrollPanel';
import {
  FORM_PAGE_DESKTOP_BP,
  GLASS_FORM_PAIR_STACK_SPACING,
  GLASS_FORM_PANEL_PADDING_SX,
  GLASS_FORM_ROOT_SX,
  GLASS_FORM_ROW_SX,
  LIQUID_GLASS_FORM_SX,
  LIQUID_GLASS_PANEL_SX,
  LIQUID_GLASS_SUBMIT_BUTTON_SX,
} from '@/components/design-studio/design-studio-glass';

const SUBMIT_URL = '/t/campaigns/public/career-applications/';
const SUCCESS_REDIRECT_URL = '/careers?submitted=1';
const MAX_RESUME_BYTES = 10 * 1024 * 1024;
const ACCEPTED_RESUME_EXTENSIONS = ['.pdf', '.doc', '.docx'];

const INITIAL_VALUES = {
  first_name: '',
  last_name: '',
  phone_number: '',
  email_address: '',
  message: '',
};

function getFileExtension(name) {
  const dot = name.lastIndexOf('.');
  return dot >= 0 ? name.slice(dot).toLowerCase() : '';
}

function CareersApplicationForm({ variant = 'glass' }) {
  const isGlass = variant === 'glass';
  const [values, setValues] = React.useState(INITIAL_VALUES);
  const [resumeFile, setResumeFile] = React.useState(null);
  const [fieldErrors, setFieldErrors] = React.useState({});
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [snack, setSnack] = React.useState({ open: false, message: '', severity: 'error' });

  const closeSnack = React.useCallback(() => {
    setSnack(prev => ({ ...prev, open: false }));
  }, []);

  const openErrorSnack = React.useCallback(message => {
    setSnack({ open: true, message, severity: 'error' });
  }, []);

  const handleChange = React.useCallback(e => {
    const { name, value } = e.target;
    setValues(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleResumeChange = React.useCallback(e => {
    const file = e.target.files?.[0] ?? null;
    setResumeFile(file);
    setFieldErrors(prev => ({ ...prev, resume: '' }));
  }, []);

  const canSubmit = React.useMemo(() => {
    return (
      values.first_name.trim() &&
      values.last_name.trim() &&
      values.phone_number.trim() &&
      values.email_address.trim() &&
      values.message.trim() &&
      resumeFile
    );
  }, [values, resumeFile]);

  const validateClient = React.useCallback(() => {
    const nextErrors = {};
    if (!resumeFile) {
      nextErrors.resume = 'Resume is required.';
    } else {
      const ext = getFileExtension(resumeFile.name);
      if (!ACCEPTED_RESUME_EXTENSIONS.includes(ext)) {
        nextErrors.resume = 'Upload a PDF or Word document (.pdf, .doc, .docx).';
      } else if (resumeFile.size > MAX_RESUME_BYTES) {
        nextErrors.resume = 'Resume must be 10 MB or smaller.';
      }
    }
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  }, [resumeFile]);

  const handleSubmit = React.useCallback(
    async e => {
      e.preventDefault();
      if (!validateClient()) {
        openErrorSnack('Please fix the highlighted fields.');
        return;
      }

      setIsSubmitting(true);
      setFieldErrors({});

      const formData = new FormData();
      Object.entries(values).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('resume', resumeFile);

      try {
        const res = await fetch(SUBMIT_URL, {
          method: 'POST',
          body: formData,
        });

        if (!res.ok) {
          let payload = null;
          try {
            payload = await res.json();
          } catch {
            payload = null;
          }

          if (payload && typeof payload === 'object') {
            const nextFieldErrors = {};
            Object.keys(payload).forEach(key => {
              const v = payload[key];
              if (Array.isArray(v) && v.length) nextFieldErrors[key] = String(v[0]);
              else if (typeof v === 'string') nextFieldErrors[key] = v;
            });
            setFieldErrors(nextFieldErrors);
            const detailMsg = typeof payload.detail === 'string' ? payload.detail : '';
            openErrorSnack(detailMsg || 'Please fix the highlighted fields.');
          } else {
            openErrorSnack('Submission failed. Please try again.');
          }
          return;
        }

        window.location.assign(SUCCESS_REDIRECT_URL);
      } catch {
        openErrorSnack('Network error. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    },
    [values, resumeFile, validateClient, openErrorSnack],
  );

  const panelSx = isGlass
    ? { ...LIQUID_GLASS_PANEL_SX, ...GLASS_FORM_PANEL_PADDING_SX }
    : { p: 2.5, borderRadius: 2, border: 1, borderColor: 'divider' };

  const rowSx = isGlass ? GLASS_FORM_ROW_SX : { mb: 2 };
  const rowSpacing = isGlass ? GLASS_FORM_PAIR_STACK_SPACING : { xs: 0, [FORM_PAGE_DESKTOP_BP]: 2 };

  const sectionHeadingSx = isGlass
    ? {
        color: 'rgba(255, 255, 255, 0.9)',
        fontSize: '0.75rem',
        fontWeight: 600,
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
        mb: { xs: 1, [FORM_PAGE_DESKTOP_BP]: 0.25 },
      }
    : { mb: 2 };

  const fileButtonSx = isGlass
    ? {
        color: 'rgba(255, 255, 255, 0.92)',
        borderColor: 'rgba(255, 255, 255, 0.45)',
        textTransform: 'none',
        justifyContent: 'flex-start',
        '&:hover': {
          borderColor: 'rgba(255, 255, 255, 0.75)',
          bgcolor: 'rgba(255, 255, 255, 0.08)',
        },
      }
    : { textTransform: 'none', justifyContent: 'flex-start' };

  const fileHelperSx = isGlass
    ? { color: 'rgba(255, 255, 255, 0.72)', fontSize: '0.75rem', mt: 0.75 }
    : { color: 'text.secondary', fontSize: '0.75rem', mt: 0.75 };

  const formHeader = (
    <Box sx={rowSx}>
      <Typography
        component="h2"
        variant={isGlass ? undefined : 'h6'}
        fontWeight={600}
        sx={sectionHeadingSx}
      >
        Application
      </Typography>
    </Box>
  );

  const formFooter = (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: isGlass ? 0.25 : 2 }}>
      <Button
        type="submit"
        variant="contained"
        size={isGlass ? 'medium' : 'large'}
        disableElevation
        disabled={isSubmitting || !canSubmit}
        sx={
          isGlass
            ? { ...LIQUID_GLASS_SUBMIT_BUTTON_SX, py: 1, minWidth: 120 }
            : { borderRadius: 2, py: 1.25 }
        }
      >
        {isSubmitting ? 'Submitting...' : 'Submit'}
      </Button>
    </Box>
  );

  const formFields = (
    <>
      <Stack
        direction={{ xs: 'column', [FORM_PAGE_DESKTOP_BP]: 'row' }}
        spacing={rowSpacing}
        sx={rowSx}
      >
        <GlassFormField
          isGlass={isGlass}
          name="first_name"
          label="First Name"
          value={values.first_name}
          onChange={handleChange}
          fullWidth
          required
          error={Boolean(fieldErrors.first_name)}
          helperText={fieldErrors.first_name}
          autoComplete="given-name"
        />
        <GlassFormField
          isGlass={isGlass}
          name="last_name"
          label="Last Name"
          value={values.last_name}
          onChange={handleChange}
          fullWidth
          required
          error={Boolean(fieldErrors.last_name)}
          helperText={fieldErrors.last_name}
          autoComplete="family-name"
        />
      </Stack>

      <Box sx={rowSx}>
        <GlassFormField
          isGlass={isGlass}
          name="phone_number"
          label="Phone Number"
          value={values.phone_number}
          onChange={handleChange}
          fullWidth
          required
          error={Boolean(fieldErrors.phone_number)}
          helperText={fieldErrors.phone_number}
          autoComplete="tel"
        />
      </Box>

      <Box sx={rowSx}>
        <GlassFormField
          isGlass={isGlass}
          name="email_address"
          label="Email Address"
          type="email"
          value={values.email_address}
          onChange={handleChange}
          fullWidth
          required
          error={Boolean(fieldErrors.email_address)}
          helperText={fieldErrors.email_address}
          autoComplete="email"
        />
      </Box>

      <Box sx={rowSx}>
        <GlassFormField
          isGlass={isGlass}
          name="message"
          label="Why do you want to join NIICO?"
          value={values.message}
          onChange={handleChange}
          fullWidth
          required
          multiline
          minRows={4}
          error={Boolean(fieldErrors.message)}
          helperText={fieldErrors.message}
          placeholder="Tell us why you love this industry and want to be part of our team."
        />
      </Box>

      <Box sx={rowSx}>
        <Button component="label" variant="outlined" fullWidth sx={fileButtonSx}>
          {resumeFile ? resumeFile.name : 'Upload Resume (PDF or Word)'}
          <input
            type="file"
            hidden
            accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
            onChange={handleResumeChange}
          />
        </Button>
        <Typography sx={fileHelperSx}>Accepted formats: PDF, DOC, DOCX. Max size 10 MB.</Typography>
        {fieldErrors.resume ? (
          <Typography sx={{ ...fileHelperSx, color: '#f44336' }}>{fieldErrors.resume}</Typography>
        ) : null}
      </Box>
    </>
  );

  const snackbar = (
    <Snackbar open={snack.open} autoHideDuration={6000} onClose={closeSnack}>
      <Alert onClose={closeSnack} severity={snack.severity} sx={{ width: '100%' }} variant="filled">
        {snack.message}
      </Alert>
    </Snackbar>
  );

  if (isGlass) {
    return (
      <Box sx={GLASS_FORM_ROOT_SX}>
        <MarketingFormPanel
          isGlass
          panelSx={panelSx}
          formSx={LIQUID_GLASS_FORM_SX}
          onSubmit={handleSubmit}
          header={formHeader}
          footer={formFooter}
        >
          {formFields}
        </MarketingFormPanel>
        {snackbar}
      </Box>
    );
  }

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      {formHeader}
      {formFields}
      {formFooter}
      {snackbar}
    </Box>
  );
}

CareersApplicationForm.propTypes = {
  variant: PropTypes.oneOf(['default', 'glass']),
};

export default CareersApplicationForm;
